/*
 * Copyright 2026 Sony Corporation
 */

import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { Device, DeviceStateResponse } from '@ips-device-verse/twin-service/schema';

import type { BomSection } from './DeployPanel';
import { rollUp } from '../../catalog/bom';
import { formatUsd } from '../../catalog/camera-models';
import type { Transform } from '../../scene/types';

export interface BomDetailProps {
  /** The gateway section has its own page; everything else is itemised here. */
  section: Exclude<BomSection, 'gateway'>;
  devices: Device[];
  poses: Record<string, Transform>;
  states: Record<string, DeviceStateResponse>;
}

const DEGREES = 180 / Math.PI;

const TITLES: Record<BomDetailProps['section'], string> = {
  bom: 'BOM',
  configuration: 'Configuration',
  pose: 'Device Pose',
};

const BomTable = ({ devices }: { devices: Device[] }) => {
  const { lines, priced, incomplete } = rollUp(devices);

  return (
    <>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Model</TableCell>
            <TableCell align="right">Number</TableCell>
            <TableCell align="right">Unit price</TableCell>
            <TableCell align="right">Price</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map((line) => (
            <TableRow key={line.model}>
              <TableCell className="font-mono">{line.model}</TableCell>
              <TableCell align="right">{line.quantity}</TableCell>
              <TableCell align="right">
                {line.unitPrice === undefined ? (
                  <Typography variant="caption" color="text.secondary">
                    no price
                  </Typography>
                ) : (
                  formatUsd(line.unitPrice)
                )}
              </TableCell>
              <TableCell align="right">{line.subtotal === undefined ? '—' : formatUsd(line.subtotal)}</TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell>
              <strong>Total</strong>
            </TableCell>
            <TableCell align="right">
              <strong>{devices.length}</strong>
            </TableCell>
            <TableCell />
            <TableCell align="right">
              <strong>{formatUsd(priced)}</strong>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      {incomplete && (
        <Typography variant="caption" color="warning.main" className="block p-3">
          Some models have no list price; the total covers the priced lines only.
        </Typography>
      )}
    </>
  );
};

const ConfigurationTable = ({
  devices,
  states,
}: {
  devices: Device[];
  states: Record<string, DeviceStateResponse>;
}) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>Device</TableCell>
        <TableCell>Model</TableCell>
        <TableCell>Serial</TableCell>
        <TableCell>Network</TableCell>
        <TableCell>Look</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {devices.map((device) => {
        const reported = states[device.device_id]?.state.reported;
        return (
          <TableRow key={device.device_id}>
            <TableCell>{device.name}</TableCell>
            <TableCell className="font-mono">{device.model}</TableCell>
            <TableCell className="font-mono">{device.serial}</TableCell>
            <TableCell className="font-mono">{device.address}:15740</TableCell>
            {/* From the shadow, so this is what the body reports rather than what was asked of it. */}
            <TableCell className="font-mono">{reported?.creativeLook?.mode ?? '—'}</TableCell>
          </TableRow>
        );
      })}
    </TableBody>
  </Table>
);

const PoseTable = ({ devices, poses }: { devices: Device[]; poses: Record<string, Transform> }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>Device</TableCell>
        <TableCell align="right">X (m)</TableCell>
        <TableCell align="right">Y (m)</TableCell>
        <TableCell align="right">Z (m)</TableCell>
        <TableCell align="right">Rot X (°)</TableCell>
        <TableCell align="right">Rot Y (°)</TableCell>
        <TableCell align="right">Rot Z (°)</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {devices.map((device) => {
        const pose = poses[device.device_id];
        if (!pose) {
          return (
            <TableRow key={device.device_id}>
              <TableCell>{device.name}</TableCell>
              <TableCell colSpan={6}>
                <Typography variant="caption" color="text.secondary">
                  Not placed yet
                </Typography>
              </TableCell>
            </TableRow>
          );
        }
        return (
          <TableRow key={device.device_id}>
            <TableCell>{device.name}</TableCell>
            <TableCell align="right" className="font-mono">
              {pose.position.x.toFixed(2)}
            </TableCell>
            <TableCell align="right" className="font-mono">
              {pose.position.y.toFixed(2)}
            </TableCell>
            <TableCell align="right" className="font-mono">
              {pose.position.z.toFixed(2)}
            </TableCell>
            <TableCell align="right" className="font-mono">
              {(pose.rotation.x * DEGREES).toFixed(1)}
            </TableCell>
            <TableCell align="right" className="font-mono">
              {(pose.rotation.y * DEGREES).toFixed(1)}
            </TableCell>
            <TableCell align="right" className="font-mono">
              {(pose.rotation.z * DEGREES).toFixed(1)}
            </TableCell>
          </TableRow>
        );
      })}
    </TableBody>
  </Table>
);

/**
 * The detail behind one bill-of-materials section, shown over the viewport.
 *
 * It covers the canvas rather than replacing it: unmounting `BabylonCanvas`
 * would dispose the engine and rebuild the whole scene graph on the way back.
 */
export const BomDetail = ({ section, devices, poses, states }: BomDetailProps) => (
  <Box className="absolute inset-0 z-10 overflow-auto p-6" sx={{ bgcolor: 'background.default' }}>
    <Typography variant="h6" className="mb-3">
      {TITLES[section]}
    </Typography>

    {devices.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        Add devices to the world and they will be itemised here.
      </Typography>
    ) : (
      <TableContainer component={Paper} variant="outlined">
        {section === 'bom' && <BomTable devices={devices} />}
        {section === 'configuration' && <ConfigurationTable devices={devices} states={states} />}
        {section === 'pose' && <PoseTable devices={devices} poses={poses} />}
      </TableContainer>
    )}
  </Box>
);
